import { CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn, StatusBadge, Text } from "@athena/ui";
import { CHANNEL_ICON, CHANNEL_TONE } from "../helpers/channelIcon";
import type { ContactEntry } from "../helpers/client";
import { CONTACT_OUTCOME_VARIANT, formatCalendarDate, formatContactTime } from "../helpers/client";

interface ContactHistoryListProps {
    /** Newest first; drawn as given. */
    contacts: ContactEntry[];
}

/**
 * Contacts as a timeline: channel, outcome, when, the note, any follow-up, and who logged it. The
 * profile's card shows the latest few through this; the full record shows every one.
 */
export function ContactHistoryList({ contacts }: ContactHistoryListProps) {
    const { t } = useTranslation();
    return (
        <ol aria-label={t("admin.alumni.history.title")} className="flex flex-col">
            {contacts.map((contact, index) => {
                const Icon = CHANNEL_ICON[contact.channel];
                const last = index === contacts.length - 1;
                return (
                    <li key={contact.id} className="flex gap-4">
                        <div className="flex shrink-0 flex-col items-center">
                            <span
                                className={cn(
                                    "flex h-9 w-9 items-center justify-center rounded-full",
                                    CHANNEL_TONE[contact.channel],
                                )}
                            >
                                <Icon className="h-3.5 w-3.5" aria-hidden />
                            </span>
                            {last ? null : <span className="bg-border my-1.5 w-0.5 flex-1" />}
                        </div>
                        <div className={last ? "min-w-0 flex-1" : "min-w-0 flex-1 pb-6"}>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <Text as="span" size="sm" weight="semibold" className="font-display">
                                    {t(`admin.alumni.channel.${contact.channel}`)}
                                </Text>
                                <StatusBadge variant={CONTACT_OUTCOME_VARIANT[contact.outcome]}>
                                    {t(`admin.alumni.outcome.${contact.outcome}`)}
                                </StatusBadge>
                                <Text as="span" size="xs" muted className="ml-auto font-mono">
                                    <time dateTime={contact.contactedAt}>{formatContactTime(contact.contactedAt)}</time>
                                </Text>
                            </div>
                            {contact.note ? (
                                <Text size="sm" className="mt-2 leading-relaxed">
                                    {contact.note}
                                </Text>
                            ) : null}
                            {contact.followUpDate || contact.contactedBy ? (
                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                                    {contact.followUpDate ? (
                                        <Text
                                            as="span"
                                            size="xs"
                                            className="text-text-heading inline-flex items-center gap-1.5 font-medium"
                                        >
                                            <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                                            {t("admin.alumni.history.followUp", {
                                                date: formatCalendarDate(contact.followUpDate),
                                            })}
                                        </Text>
                                    ) : null}
                                    {/* Empty when the coordinator who logged it has since been removed. */}
                                    {contact.contactedBy ? (
                                        <Text as="span" size="xs" muted>
                                            {t("admin.alumni.history.contactedBy", {
                                                name: contact.contactedBy,
                                            })}
                                        </Text>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
